import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * Calls `onPause` when the app goes to background (native) or tab becomes hidden / unloads (web).
 * `onPauseSync` is used for the `beforeunload` event on web where only synchronous work is reliable.
 */
export function useAppLifecycle(
  onPause: () => void,
  onPauseSync: () => void
) {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const listener = App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) onPause();
      });
      return () => { listener.then(l => l.remove()); };
    } else {
      const handleVisibility = () => {
        if (document.visibilityState === 'hidden') onPause();
      };
      const handleBeforeUnload = () => {
        onPauseSync();
      };

      document.addEventListener('visibilitychange', handleVisibility);
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [onPause, onPauseSync]);
}
